/**
 * Validated input components for forms
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, ChevronDown } from 'lucide-react';
import { ValidationError } from '@/utils/validation';

interface ValidatedInputProps {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (value: string) => ValidationError | null;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
  className?: string;
  helpText?: string;
}

export const ValidatedInput: React.FC<ValidatedInputProps> = ({
  label,
  type = 'text',
  value,
  onChange,
  onValidate,
  placeholder,
  disabled = false,
  required = false,
  maxLength,
  className = '',
  helpText
}) => {
  const [error, setError] = useState<ValidationError | null>(null);
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (onValidate && touched) {
      const validationError = onValidate(value);
      setError(validationError);
      setIsValid(!validationError && value.trim() !== '');
    }
  }, [value, onValidate, touched]);

  const handleBlur = () => {
    setTouched(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) return;
    onChange(newValue);
  };

  const inputClasses = `
    w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border 
    focus:outline-none focus:ring-1 transition-all duration-200
    ${error && touched 
      ? 'border-red-300 dark:border-red-700 focus:ring-red-300 dark:focus:ring-red-700' 
      : isValid && touched
      ? 'border-green-300 dark:border-green-700 focus:ring-green-300 dark:focus:ring-green-700'
      : 'border-gray-200 dark:border-gray-800 focus:ring-gray-300 dark:focus:ring-gray-700'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type={type}
          className={inputClasses}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
        />
        
        {/* Validation icons */}
        {touched && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {error ? (
              <AlertCircle className="h-4 w-4 text-red-500" />
            ) : isValid ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : null}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && touched && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error.message}
        </p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}

      {/* Character count */}
      {maxLength && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
};

interface ValidatedTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (value: string) => ValidationError | null;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
  rows?: number;
  className?: string;
  helpText?: string;
}

export const ValidatedTextarea: React.FC<ValidatedTextareaProps> = ({
  label,
  value,
  onChange,
  onValidate,
  placeholder,
  disabled = false,
  required = false,
  maxLength,
  rows = 4,
  className = '',
  helpText
}) => {
  const [error, setError] = useState<ValidationError | null>(null);
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (onValidate && touched) {
      const validationError = onValidate(value);
      setError(validationError);
      setIsValid(!validationError && value.trim() !== '');
    }
  }, [value, onValidate, touched]);

  const handleBlur = () => {
    setTouched(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) return;
    onChange(newValue);
  };

  const textareaClasses = `
    w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border 
    focus:outline-none focus:ring-1 transition-all duration-200 resize-none
    ${error && touched 
      ? 'border-red-300 dark:border-red-700 focus:ring-red-300 dark:focus:ring-red-700' 
      : isValid && touched
      ? 'border-green-300 dark:border-green-700 focus:ring-green-300 dark:focus:ring-green-700'
      : 'border-gray-200 dark:border-gray-800 focus:ring-gray-300 dark:focus:ring-gray-700'
    }
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <textarea
          className={textareaClasses}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          rows={rows}
        />
        
        {/* Validation icons */}
        {touched && (
          <div className="absolute top-2 right-2">
            {error ? (
              <AlertCircle className="h-4 w-4 text-red-500" />
            ) : isValid ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : null}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && touched && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error.message}
        </p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}

      {/* Character count */}
      {maxLength && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
};

interface SelectOption {
  value: string;
  label: string;
}

interface ValidatedSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  helpText?: string;
}

export const ValidatedSelect: React.FC<ValidatedSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  required = false,
  className = '',
  helpText
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find(option => option.value === value);

  const selectClasses = `
    w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border 
    border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 
    focus:ring-gray-300 dark:focus:ring-gray-700 cursor-pointer
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <div
          className={selectClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <span className={selectedOption ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {options.map((option) => (
              <div
                key={option.value}
                className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer text-sm"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help text */}
      {helpText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
